
import { Options, Vue } from "vue-class-component";
import { RestrictedAreasService } from "@/services/RestrictedAreasService";
import { MapService } from "@/services/MapService";
import { watch } from "@vue/runtime-core";
import { LoaderService } from "@/services/LoaderService";
import { ContextService } from "@/services/ContextService";
import { IOverlay } from "@/interfaces/IOverlay";
import { store } from "@/store";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import FeatureTable from "@arcgis/core/widgets/FeatureTable";
import FieldConfig from "@arcgis/core/widgets/FeatureForm/FieldConfig";
import { IRestrictedArea } from "@/interfaces/IRestrictedArea";
import { NotificationService } from "@/services/NotificationService";
import eventBus from "@/services/EventBus";
import EsriGeoJSONUtils from "@/utils/EsriGeoJSONUtils";

@Options({
  props: {},
})
export default class RestrictedAreas extends Vue {
  overlay: IOverlay = {} as IOverlay;
  onAreaAdded: number;
  isEditor = false;
  tableSize = { size: 0 };
  tableSelectedItems = { size: 0};
  showFT = false;
  overwriteData = false;
  appendData = true;
  size = 0;
  allLayers: any;
  featureLayer: FeatureLayer;
  featureTable: FeatureTable;
  tableFieldConfig: FieldConfig[] = [
    {
      name: "Name",
      label: "Name",
    } as FieldConfig,
    {
      name: "StartDate",
      label: "Start",
    } as FieldConfig,
    {
      name: "EndDate",
      label: "End",
    } as FieldConfig,
    {
      name: "Car",
      label: "Car",
    } as FieldConfig,
    {
      name: "Bus",
      label: "Bus",
    } as FieldConfig,
    {
      name: "Truck",
      label: "Truck",
    } as FieldConfig,
    {
      name: "Pedestrian",
      label: "Pedestrian",
    } as FieldConfig,
    {
      name: "Comments",
      label: "Comments",
    } as FieldConfig,
  ];
  exportType = "Layer";

  mounted() {
    this.overlay = { ...store.state.overlay };
    RestrictedAreasService.getInstance().showFeatureTable(
      (this.$refs as any).featureTableRef,
      this.overlay
    );
    this.isEditor = ContextService.getInstance().isEditor();
    this.tableSize = RestrictedAreasService.getInstance().tableSize;
    this.tableSelectedItems = RestrictedAreasService.getInstance().tableSelectedItems;
    this.allLayers = MapService.getInstance().map.allLayers;
    eventBus().emitter.on("addLayer", () => {
      this.allLayers = MapService.getInstance().map.allLayers;
      this.$forceUpdate();
    });
  }

  onNewArea(): void {
    if (this.overlay.RoutingServiceVersion === "V8")
      MapService.getInstance().startDrawing("areaV8");
    else
      MapService.getInstance().startDrawing(
        "area",
        this.overlay.RoutingServiceVersion
      );
  }

  async onRemoveArea(): Promise<void> {
    LoaderService.getInstance().showLoader();
    try {
      await RestrictedAreasService.getInstance().deleteSelectedArea();
    } finally {
      LoaderService.getInstance().hideLoader();
    }
  }

  showImportModal(): void {
    (this.$refs as any).importModal.active = true;
  }

  hideImportModal(): void {
    (this.$refs as any).importModal.active = false;
  }

  showExportModal(): void {
    (this.$refs as any).exportModal.active = true;
  }

  hideExportModal(): void {
    (this.$refs as any).exportModal.active = false;
  }

  showFeatureTable(layer: any): void {
    if (this.featureTable) {
      this.featureTable.view = MapService.getInstance().mapView;
      this.featureTable.layer = layer;
      this.featureTable.fieldConfigs = this.tableFieldConfig as any;
      this.featureTable.container = (this.$refs as any).featureTableRef2;
      this.featureTable.visible = true;
    } else {
      this.featureTable = new FeatureTable({
        view: MapService.getInstance().mapView,
        layer: layer,
        fieldConfigs: this.tableFieldConfig,
        container: (this.$refs as any).featureTableRef2,
      });
    }
    this.featureTable.on("selection-change", (evt) => {
      if (evt.removed.length < 1 || evt.added.length < 1) {
        this.size = (this.featureTable as any).grid.selectedItems.length;
      }
    });
  }

  hideFeatureTable(): void {
    this.featureTable.visible = false;
  }

  async importData(): Promise<void> {
    const areaGrid = (this.featureTable as any).grid;
    if (this.appendData === true && this.overwriteData === false) {
      if (areaGrid && areaGrid.selectedItems && areaGrid.selectedItems.items) {
        LoaderService.getInstance().showLoader();
        try {
          const query = this.featureTable.layer.createQuery();
          query.where = "1=1";
          query.outFields = ["*"];
          const areas = await this.featureTable.layer.queryFeatures(query);
          areas.features.forEach(async (element: any) => {
            const area: IRestrictedArea = {
              Shape__Area: element.attributes.Shape__Area,
              Shape__Length: element.attributes.Shape__Length,
              CreationDate: element.attributes.CreationDate,
              Creator: element.attributes.Creator,
              EditDate: element.attributes.EditDate,
              Editor: element.attributes.Editor,
              Name: element.attributes.Name,
              StartDate: element.attributes.StartDate,
              EndDate: element.attributes.EndDate,
              Comments: element.attributes.Comments,
              OVERLAYID: store.state.overlay.OBJECTID,
              TransportMode: element.attributes.TransportMode,
              Car: element.attributes.Car,
              Bus: element.attributes.Bus,
              Truck: element.attributes.Truck,
              Pedestrian: element.attributes.Pedestrian,
              BoundingBox: element.attributes.BoundingBox,
              OBJECTID: element.attributes.OBJECTID,
              geometry: element.geometry
            } as IRestrictedArea;
            await RestrictedAreasService.getInstance().addRestrictedArea(area);
          });
        } catch (error) {
          LoaderService.getInstance().hideLoader();
          NotificationService.getInstance().showNotification(
            "Import Restricted Areas",
            error.data.error,
            "red",
            false
          );
        } finally {
          LoaderService.getInstance().hideLoader();
          NotificationService.getInstance().showNotification(
            "Import Restricted Areas",
            "The data has been successfully added",
            "green",
            false
          );
        }
      }
    } else if (this.overwriteData === true && this.appendData === false) {
      if (areaGrid && areaGrid.selectedItems && areaGrid.selectedItems.items) {
        LoaderService.getInstance().showLoader();
        try {
          const areas =
            await RestrictedAreasService.getInstance().getCurrentRestrictedAreas();
          areas.forEach(async (element: any) => {
            await RestrictedAreasService.getInstance().deleteRestrictedArea(
              element.OBJECTID
            );
          });
          const query = this.featureTable.layer.createQuery();
          query.where = "1=1";
          query.outFields = ["*"];
          const areass = await this.featureTable.layer.queryFeatures(query);
          areass.features.forEach(async (element: any) => {
            const area: IRestrictedArea = {
              Shape__Area: element.attributes.Shape__Area,
              Shape__Length: element.attributes.Shape__Length,
              CreationDate: element.attributes.CreationDate,
              Creator: element.attributes.Creator,
              EditDate: element.attributes.EditDate,
              Editor: element.attributes.Editor,
              Name: element.attributes.Name,
              StartDate: element.attributes.StartDate,
              EndDate: element.attributes.EndDate,
              Comments: element.attributes.Comments,
              OVERLAYID: store.state.overlay.OBJECTID,
              TransportMode: element.attributes.TransportMode,
              Car: element.attributes.Car,
              Bus: element.attributes.Bus,
              Truck: element.attributes.Truck,
              Pedestrian: element.attributes.Pedestrian,
              BoundingBox: element.attributes.BoundingBox,
              OBJECTID: element.attributes.OBJECTID,
              geometry: element.geometry
            } as IRestrictedArea;
            await RestrictedAreasService.getInstance().addRestrictedArea(area);
          });
        } catch (error) {
          LoaderService.getInstance().hideLoader();
          NotificationService.getInstance().showNotification(
            "Import Restricted Areas",
            error.data.error,
            "red",
            false
          );
        } finally {
          LoaderService.getInstance().hideLoader();
          NotificationService.getInstance().showNotification(
            "Import Restricted Areas",
            "The data has been successfully overwritten",
            "green",
            false
          );
        }
      }
    }
  }

  async exportData(): Promise<void> {
    let layer;
    let features: any[] = [];
    try {
      LoaderService.getInstance().showLoader();
      if (this.exportType == "Layer") {
        const query = RestrictedAreasService.getInstance().featureLayer.createQuery();
        query.where = "OVERLAYID = '" + store.state.overlay.OBJECTID + "'";
        query.outFields = ["*"];
        const response = await RestrictedAreasService.getInstance().featureLayer.queryFeatures(query);
        response.features.forEach((f: any) => {
          const feat = {
            type: "Feature",
            objectId: f.attributes.OBJECTID,
            geometry: {
              type: (
                f.geometry.type.charAt(0).toUpperCase() +
                f.geometry.type.slice(1)
                ).toString(),
              coordinates: f.geometry.rings
            },
            properties: {
              Shape__Area: f.attributes.Shape__Area,
              Shape__Length: f.attributes.Shape__Length,
              CreationDate: f.attributes.CreationDate,
              Creator: f.attributes.Creator,
              EditDate: f.attributes.EditDate,
              Editor: f.attributes.Editor,
              Name: f.attributes.Name,
              StartDate:f.attributes.StartDate,
              EndDate: f.attributes.EndDate,
              Comments: f.attributes.Comments,
              OVERLAYID: store.state.overlay.OBJECTID,
              TransportMode: f.attributes.TransportMode,
              Car: f.attributes.Car,
              Bus: f.attributes.Bus,
              Truck: f.attributes.Truck,
              Pedestrian: f.attributes.Pedestrian,
              BoundingBox: f.attributes.BoundingBox,
              OBJECTID: f.attributes.OBJECTID,
            },
          };
          features.push(feat);
        });
        layer = {
          type: "FeatureCollection",
          crs: {
            type: "name",
            properties: {
              name: "EPSG:4326",
            },
          },
          features: features,
        };
      } else if (
        this.exportType == "Selected" &&
        (RestrictedAreasService.getInstance().featureTable as any).grid.selectedItems.length > 0
      ) {
        const table = (RestrictedAreasService.getInstance().featureTable as any).grid.selectedItems.items;
        let where;
        if ((RestrictedAreasService.getInstance().featureTable as any).grid.selectedItems.length > 1) {
          where = "OBJECTID in ";
          const list: any[] = [];
          table.forEach((feat: any) => {
            list.push(feat.feature.attributes.OBJECTID);
          });
          where += "(" + list.join(", ") + ")";
        } else if ((RestrictedAreasService.getInstance().featureTable as any).grid.selectedItems.length == 1) {
          where = "OBJECTID = '" + table[0].feature.attributes.OBJECTID + "'";
        }
        const query = RestrictedAreasService.getInstance().featureLayer.createQuery();
        query.where = where;
        query.outFields = ["*"];
        const response = await RestrictedAreasService.getInstance().featureLayer.queryFeatures(query);
        response.features.forEach((f: any) => {
          const feat = {
            type: "Feature",
            objectId: f.attributes.OBJECTID,
           geometry: EsriGeoJSONUtils.arcgisToGeoJSON(f.geometry),
            properties: {
              Shape__Area: f.attributes.Shape__Area,
              Shape__Length: f.attributes.Shape__Length,
              CreationDate: f.attributes.CreationDate,
              Creator: f.attributes.Creator,
              EditDate: f.attributes.EditDate,
              Editor: f.attributes.Editor,
              Name: f.attributes.Name,
              StartDate:f.attributes.StartDate,
              EndDate: f.attributes.EndDate,
              Comments: f.attributes.Comments,
              OVERLAYID: store.state.overlay.OBJECTID,
              TransportMode: f.attributes.TransportMode,
              Car: f.attributes.Car,
              Bus: f.attributes.Bus,
              Truck: f.attributes.Truck,
              Pedestrian: f.attributes.Pedestrian,
              BoundingBox: f.attributes.BoundingBox,
              OBJECTID: f.attributes.OBJECTID,
            },
          };
          features.push(feat);
        });
        layer = {
          type: "FeatureCollection",
          crs: {
            type: "name",
            properties: {
              name: "EPSG:4326",
            },
          },
          features: features,
        };
      }
      if (
        this.exportType == "Layer" ||
        (this.exportType == "Selected" &&
          (RestrictedAreasService.getInstance().featureTable as any).grid.selectedItems.length > 0)
      ) {
        let name = "Restricted Areas - ";
        name += Date.now().toString();
        var hiddenElement = document.createElement("a");
        var data = new Blob([JSON.stringify(layer)], { type: "text/plain" });
        var url = window.URL.createObjectURL(data);
        hiddenElement.href = url;
        hiddenElement.target = "_blank";
        hiddenElement.download = name + ".geojson";
        hiddenElement.click();
      }
    } catch (error) {
      LoaderService.getInstance().hideLoader();
      NotificationService.getInstance().showNotification(
        "Export Restricted Areas",
        "Can't export the data.",
        "red",
        false
      );
    } finally {
      if (
        (RestrictedAreasService.getInstance().featureTable as any).grid.selectedItems.length == 0 &&
        this.exportType == "Selected"
      ) {
        LoaderService.getInstance().hideLoader();
        NotificationService.getInstance().showNotification(
          "Export Restricted Areas",
          "Impossible to export the data because there is no selected item in the table. Please select at least one item.",
          "red",
          false
        );
      } else {
        LoaderService.getInstance().hideLoader();
        NotificationService.getInstance().showNotification(
          "Export Restricted Areas",
          "The data has been exported successfully.",
          "green",
          false
        );
      }
    }
  }

  get isDisable() {
    if (this.size < 1 || this.size > 2000) return true;
    else return false;
  }

}
